import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  Package,
  DollarSign,
  Users,
  Building2,
  TrendingUp,
  ArrowUpRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROLES, TICKET_STATUSES, TICKET_CATEGORIES, URGENCY_LEVELS, formatCurrency, formatDate } from '../lib/constants';

const StatCard = ({ title, value, icon: Icon, color = 'bg-primary/10 text-primary', testId }) => (
  <Card className="border-border hover:-translate-y-1 transition-transform" data-testid={testId}>
    <CardContent className="p-6">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${color}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">{title}</p>
      <p className="text-3xl font-medium tracking-tight text-foreground">{value}</p>
    </CardContent>
  </Card>
);

const DashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [recentTickets, setRecentTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, ticketsRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/tickets'),
      ]);
      setStats(statsRes.data);
      setRecentTickets(ticketsRes.data.slice(0, 5));
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatsForRole = () => {
    switch (user?.role) {
      case 'manager_cabang':
        return [
          { title: 'Total Tiket', value: stats.total_tickets || 0, icon: Ticket, color: 'bg-primary/10 text-primary' },
          { title: 'Menunggu Approval', value: stats.pending_tickets || 0, icon: Clock, color: 'bg-amber-100 text-amber-700' },
          { title: 'Sedang Dikerjakan', value: stats.in_progress_tickets || 0, icon: AlertCircle, color: 'bg-blue-100 text-blue-700' },
          { title: 'Selesai', value: stats.completed_tickets || 0, icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
        ];
      case 'admin_maintenance':
      case 'admin_it':
      case 'admin_purchasing':
        return [
          { title: 'Total Tiket', value: stats.total_tickets || 0, icon: Ticket, color: 'bg-primary/10 text-primary' },
          { title: 'Menunggu Approval', value: stats.pending_approval || 0, icon: Clock, color: 'bg-amber-100 text-amber-700' },
          { title: 'Sedang Dikerjakan', value: stats.in_progress || 0, icon: AlertCircle, color: 'bg-blue-100 text-blue-700' },
          { title: 'Selesai', value: stats.completed || 0, icon: CheckCircle2, color: 'bg-green-100 text-green-700' },
        ];
      case 'admin_gudang':
        return [
          { title: 'Total Barang', value: stats.total_items || 0, icon: Package, color: 'bg-primary/10 text-primary' },
          { title: 'Stok Menipis', value: stats.low_stock_items || 0, icon: AlertCircle, color: 'bg-red-100 text-red-700' },
          { title: 'Total Lokasi', value: stats.total_locations || 0, icon: Building2, color: 'bg-blue-100 text-blue-700' },
        ];
      case 'finance':
        return [
          { title: 'Total Invoice', value: stats.total_invoices || 0, icon: DollarSign, color: 'bg-primary/10 text-primary' },
          { title: 'Menunggu Approval', value: stats.pending_invoices || 0, icon: Clock, color: 'bg-amber-100 text-amber-700' },
          { title: 'Total Revenue', value: formatCurrency(stats.total_revenue), icon: TrendingUp, color: 'bg-green-100 text-green-700' },
        ];
      case 'super_admin':
        return [
          { title: 'Total Tiket', value: stats.total_tickets || 0, icon: Ticket, color: 'bg-primary/10 text-primary' },
          { title: 'Total User', value: stats.total_users || 0, icon: Users, color: 'bg-blue-100 text-blue-700' },
          { title: 'Total Lokasi', value: stats.total_locations || 0, icon: Building2, color: 'bg-purple-100 text-purple-700' },
          { title: 'Total Barang', value: stats.total_items || 0, icon: Package, color: 'bg-green-100 text-green-700' },
          { title: 'Menunggu Approval', value: stats.pending_approvals || 0, icon: Clock, color: 'bg-amber-100 text-amber-700' },
        ];
      default:
        return [];
    }
  };

  const statCards = getStatsForRole();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Header */}
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Dashboard</p>
          <h1 className="text-4xl font-medium tracking-tight mb-2">
            Selamat datang, {user?.name?.split(' ')[0]}
          </h1>
          <p className="text-muted-foreground">
            {ROLES[user?.role] || user?.role} • {formatDate(new Date().toISOString()).split(',')[0]}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {statCards.map((stat, idx) => (
            <StatCard
              key={idx}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              color={stat.color}
              testId={`stat-card-${idx}`}
            />
          ))}
        </div>

        {/* Recent Tickets */}
        <Card className="border-border">
          <CardHeader className="flex flex-row items-center justify-between border-b border-border">
            <div>
              <CardTitle className="text-xl tracking-tight font-medium">Tiket Terbaru</CardTitle>
              <p className="text-sm text-muted-foreground mt-1">5 tiket paling baru</p>
            </div>
            <Link
              to="/tickets"
              className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
              data-testid="view-all-tickets-link"
            >
              Lihat Semua <ArrowUpRight className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {recentTickets.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Ticket className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>Belum ada tiket</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {recentTickets.map((ticket) => (
                  <Link
                    key={ticket.ticket_number}
                    to={`/tickets/${ticket.ticket_number}`}
                    className="flex items-center justify-between p-4 hover:bg-secondary/50 transition-colors"
                    data-testid={`recent-ticket-${ticket.ticket_number}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-xs text-muted-foreground">{ticket.ticket_number}</span>
                        <Badge variant="outline" className={URGENCY_LEVELS[ticket.urgency]?.color}>
                          {URGENCY_LEVELS[ticket.urgency]?.label}
                        </Badge>
                      </div>
                      <p className="font-medium truncate">{ticket.title}</p>
                      <p className="text-sm text-muted-foreground truncate">
                        {TICKET_CATEGORIES[ticket.category]} • {ticket.location_name}
                      </p>
                    </div>
                    <Badge className={TICKET_STATUSES[ticket.status]?.color}>
                      {TICKET_STATUSES[ticket.status]?.label || ticket.status}
                    </Badge>
                  </Link>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default DashboardPage;
